<script setup lang="ts">
import { ref } from 'vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import AiBadgePill from './AiBadgePill.vue';
import { api } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { SEVERITY_LABEL, SEVERITY_BADGE, CATEGORY_LABEL, SOURCE_LABEL } from './issue-enums';

type CreateIssueRequest = components['schemas']['CreateIssueRequest'];
type IssueDetailDto = components['schemas']['IssueDetailDto'];

const emit = defineEmits<{
  close: [];
  created: [issue: IssueDetailDto];
}>();

interface IssueForm {
  title: string;
  summary: string | null;
  severity: number;
  category: number;
  source: number;
  reporterDisplay: string;
  location: string | null;
  aiClassify: boolean;
}

const form = ref<IssueForm>({
  title: '',
  summary: null,
  severity: 1, // Medium default
  category: 6, // Other default
  source: 0,   // Portal default
  reporterDisplay: '',
  location: null,
  aiClassify: true,
});

const isSubmitting = ref(false);
const error = ref<string | null>(null);
const createdWithAi = ref(false);
const aiResult = ref<{ severity: number; category: number; confidence: number } | null>(null);

const SEVERITIES = [0, 1, 2, 3] as const;
const CATEGORIES = [0, 1, 2, 3, 4, 5, 6] as const;
const SOURCES = [0, 1, 2, 3, 4] as const;

async function submit() {
  if (!form.value.title.trim()) {
    error.value = 'Title is required.';
    return;
  }
  isSubmitting.value = true;
  error.value = null;
  try {
    const { data, error: apiError } = await api.POST('/v1/issues', {
      body: form.value as CreateIssueRequest,
    });
    if (apiError || !data) {
      error.value = 'Failed to create issue.';
      return;
    }
    // Check if AI badge should be shown (per spec §3.2)
    if (data.aiClassified && data.aiConfidence !== null && data.aiConfidence !== undefined && data.aiConfidence > 0) {
      createdWithAi.value = true;
      aiResult.value = {
        severity: data.aiSeverity ?? data.severity,
        category: data.aiCategory ?? data.category,
        confidence: data.aiConfidence,
      };
    }
    emit('created', data);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to create issue.';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <!-- Modal backdrop -->
  <div
    class="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
    aria-label="New issue"
    data-testid="new-issue-modal"
    @click.self="emit('close')"
  >
    <div class="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div class="flex items-center justify-between p-4 border-b border-border">
        <h2 class="text-section text-fg font-semibold">New issue</h2>
        <button
          class="text-muted hover:text-fg transition-colors"
          aria-label="Close"
          @click="emit('close')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form class="p-4 flex flex-col gap-4" @submit.prevent="submit">
        <!-- AI classify result toast (only shows when aiClassified: true and confidence > 0) -->
        <div
          v-if="createdWithAi && aiResult"
          class="flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg"
        >
          <AiBadgePill label="AI SUMMARY" />
          <span class="text-caption text-teal-700">
            Classified as
            <strong>{{ SEVERITY_LABEL[aiResult.severity] }}</strong> /
            <strong>{{ CATEGORY_LABEL[aiResult.category] ?? 'Other' }}</strong>
            with
            <span class="font-mono tabular-nums">{{ aiResult.confidence }}%</span>
            confidence
          </span>
        </div>

        <!-- Title -->
        <div>
          <label class="block text-caption font-medium text-fg mb-1" for="new-issue-title">Title *</label>
          <input
            id="new-issue-title"
            v-model="form.title"
            type="text"
            maxlength="500"
            required
            placeholder="Brief description of the issue"
            class="w-full h-9 px-3 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
          >
        </div>

        <!-- Summary -->
        <div>
          <label class="block text-caption font-medium text-fg mb-1" for="new-issue-summary">Summary</label>
          <textarea
            id="new-issue-summary"
            v-model="form.summary"
            rows="3"
            placeholder="Additional details…"
            class="w-full px-3 py-2 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600 resize-none"
          />
        </div>

        <!-- Severity + Category row -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-caption font-medium text-fg mb-1" for="new-issue-severity">Severity</label>
            <select
              id="new-issue-severity"
              v-model.number="form.severity"
              class="w-full h-9 px-2 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
            >
              <option v-for="s in SEVERITIES" :key="s" :value="s">
                {{ SEVERITY_LABEL[s] }}
              </option>
            </select>
            <BaseBadge :variant="SEVERITY_BADGE[form.severity] ?? 'info'" class="mt-1">
              {{ SEVERITY_LABEL[form.severity] }}
            </BaseBadge>
          </div>
          <div>
            <label class="block text-caption font-medium text-fg mb-1" for="new-issue-category">Category</label>
            <select
              id="new-issue-category"
              v-model.number="form.category"
              class="w-full h-9 px-2 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
            >
              <option v-for="c in CATEGORIES" :key="c" :value="c">
                {{ CATEGORY_LABEL[c] }}
              </option>
            </select>
          </div>
        </div>

        <!-- Source + Reporter row -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-caption font-medium text-fg mb-1" for="new-issue-source">Source</label>
            <select
              id="new-issue-source"
              v-model.number="form.source"
              class="w-full h-9 px-2 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
            >
              <option v-for="s in SOURCES" :key="s" :value="s">
                {{ SOURCE_LABEL[s] }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-caption font-medium text-fg mb-1" for="new-issue-reporter">Reporter</label>
            <input
              id="new-issue-reporter"
              v-model="form.reporterDisplay"
              type="text"
              placeholder="Anonymous"
              class="w-full h-9 px-3 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
            >
          </div>
        </div>

        <!-- Location -->
        <div>
          <label class="block text-caption font-medium text-fg mb-1" for="new-issue-location">Location</label>
          <input
            id="new-issue-location"
            v-model="form.location"
            type="text"
            placeholder="e.g. Front gate, Pool area…"
            class="w-full h-9 px-3 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
          >
        </div>

        <!-- AI classify toggle -->
        <label class="flex items-center gap-3 cursor-pointer select-none">
          <div class="relative">
            <input
              v-model="form.aiClassify"
              type="checkbox"
              class="sr-only peer"
            >
            <div class="w-10 h-5 bg-border rounded-full peer-checked:bg-teal-600 transition-colors" />
            <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
          </div>
          <span class="text-body text-fg">
            Auto-classify with AI
            <span class="text-caption text-muted ml-1">(teal badge shown when confidence &gt; 0)</span>
          </span>
        </label>

        <!-- Error -->
        <p v-if="error" class="text-caption text-red-600" role="alert">{{ error }}</p>

        <!-- Actions -->
        <div class="flex gap-2 justify-end pt-2 border-t border-border">
          <BaseButton variant="secondary" type="button" @click="emit('close')">Cancel</BaseButton>
          <BaseButton variant="primary" type="submit" :loading="isSubmitting">
            {{ isSubmitting ? 'Creating…' : 'Create issue' }}
          </BaseButton>
        </div>
      </form>
    </div>
  </div>
</template>
